import { chevronForwardOutline } from 'ionicons/icons';
import { chevronDownOutline } from 'ionicons/icons';
import { addIcons } from 'ionicons';
import { NgFor, NgIf } from '@angular/common';
import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ModalController } from '@ionic/angular/standalone';
import { NgSelectComponent } from '@ng-select/ng-select';
import { TranslatePipe } from '@ngx-translate/core';
import { debounceTime, Subject, takeUntil } from 'rxjs';
import { HelpPageUserDto, TowerGroupDto } from 'src/app/api/models';
import { UserHelpApiService } from 'src/app/api/services';
import { StorageService } from 'src/app/core/services/storage.service';
import { StorageEnums } from 'src/shared/models/enums';
import { CustomSpinnerService } from 'src/shared/services/helpers/custom-spinner.service';
import { EventsService } from 'src/shared/services/helpers/events.service';
import { IconComponent } from "../../elements/icon/icon.component";
import { IonIcon } from "@ionic/angular/standalone";
import { ContextHelpDetailsComponent } from "../context-help-details/context-help-details.component";
import { FormsModule } from '@angular/forms';

enum ContextHelpType {
	Details = 'details',
	List = 'list',
}

@Component({
  selector: 'vl-context-help-list',
  templateUrl: './context-help-list.component.html',
  styleUrls: ['./context-help-list.component.scss'],
  imports: [IonIcon, NgSelectComponent, NgFor, FormsModule, NgIf, TranslatePipe, IconComponent, ContextHelpDetailsComponent],
})

export class ContextHelpListComponent implements OnInit, OnDestroy {
  @ViewChild('select', { static: false }) select: NgSelectComponent;
  @Input() openDetails: boolean = false;
  @Input() helpPage: HelpPageUserDto;
  @Input() language: string;
  @Input() showContactUs: boolean;
  @Input() group: TowerGroupDto;
  @Input() showBackBtn: boolean = true;
  @Input() pageView: 'web' | 'mobile';
  @Input() key: string;
  contextHelpList: HelpPageUserDto[] = [];
  currentType: ContextHelpType = ContextHelpType.List;
  tagsList: string[] = [];
  selectedTags: [] = [];
  loading: boolean = true;
  currentPageIndex: number;
  currentPageSize = 10;
	private page = 0;
  disableLoadMore: boolean = false;
  hideNext: boolean = false;
  hidePrevious: boolean = false;
  private notifier: Subject<any> = new Subject<any>();
  private searchSubject = new Subject<void>();

  constructor(
    private contextHelpService: UserHelpApiService,
    private modalCtrl: ModalController,
    private eventsService: EventsService,
    private storageService:StorageService,
    private loader: CustomSpinnerService
  ) {
    addIcons({ chevronForwardOutline, chevronDownOutline });
  }

  async ngOnInit() {
    this.language = this.language || (await this.storageService.getItem(StorageEnums.LANG))?.toUpperCase();
    if(this.helpPage) {
      this.currentType = ContextHelpType.Details;
    } else {
      this.showList();
    }
    if(this.pageView === 'web') {
      this.contextHelpService.getHelpPagebyRoute({key: this.key, language: this.language, web: true}).subscribe(v => {
        this.helpPage = v;
        this.currentType = ContextHelpType.Details;
      });
    }
    this.eventsService.$langUpdateObs
			.pipe(takeUntil(this.notifier))
			.subscribe(async () => {
        this.language = (await this.storageService.getItem(StorageEnums.LANG))?.toUpperCase();
        this.getHelpPages(false, true);
        this.contextHelpService.getHelpPage({id: this.helpPage?.id, language: this.language}).subscribe(v => {
          this.helpPage = v;
        })
    });
    this.searchSubject.pipe(
      debounceTime(200)
    ).subscribe(() => {
      this.getHelpPages(true, true);
    });
  }

  public ngOnDestroy(): void {
		this.notifier.next('');
		this.notifier.complete();
	}

  getHelpPages(searchable: boolean = false, initial: boolean = false) {
    if(initial) {
      this.page = 0;
      this.disableLoadMore = false;
    }
    this.loading = true;
    this.loader.show();
    this.contextHelpService.getHelpPages({language: this.language, web: true, page: this.page, pageSize: this.currentPageSize, tags: this.selectedTags}).subscribe((r)=> {
      this.contextHelpList = initial ? r.elements: [...this?.contextHelpList, ...r.elements];
      this.loader.hide();
      this.loading = false;
      this.page++;
      if(searchable && (this.pageView === 'web') && r?.elements[0]) {
        this.helpPage = r?.elements[0];
      }
      if(!r.elements.length || (r.elements.length < this.currentPageSize) || this.contextHelpList?.length === r.total) {
        this.disableLoadMore = true;
      }
    }, err => {
      this.disableLoadMore = true;
    });
  }

  postTrackBy(index: number, n) {
    return n.id;
  }

  resetButtons() {
    this.hidePrevious = false;
    this.hideNext = false;
  }

  async getTags() {
    const body: any = {
      web:true
    };
    if(this.language !== 'EN') body.language = this.language;
    this.contextHelpService.getHelpPageTagCloud(body).subscribe((tags) => {
      this.tagsList = tags.value;
    });
  }

  getPageDetails(page: HelpPageUserDto) {
    this.currentType = ContextHelpType.Details;
    this.helpPage = page;
  }

  showList() {
    this.currentType = ContextHelpType.List;
    this.currentPageSize = this.pageView ? 8: 10;
    this.resetButtons();
    this.getTags();
    this.getHelpPages(false, true);
  }

  close() {
    this.modalCtrl.dismiss();
  }

  handleClear(event) {
    if (event) {
      event.preventDefault(); // Prevent default touch behavior
    }
    this.search();

    if (this.select) {
      this.select.detectChanges();
      this.select.clearModel();
      this.select.close();
    }
  }

  onFocus() {
    const clearButton = document.querySelector('.ng-clear');
    if (clearButton) {
      clearButton.addEventListener('click', (event) => {
        event.stopPropagation(); // Prevent event bubbling
        this.handleClear(event);
      });
    }
  }

  search() {
    this.searchSubject.next();
  }

  nextPage(id: number) {
    this.loader.show();
    this.contextHelpService.getHelpPageNext({id, web: true, language: this.language}).subscribe(p => {
      this.helpPage = p;
      this.resetButtons();
      this.loader.hide();
    }, err => {
      this.hideNext = true;
      this.loader.hide();
    });
  }

  previousPage(id: number) {
    this.loader.show();
    this.contextHelpService.getHelpPagePrevious({id, web: true, language: this.language}).subscribe(p => {
      this.helpPage = p;
      this.resetButtons();
      this.loader.hide();
    }, err => {
      this.hidePrevious = true;
      this.loader.hide();
    });
  }

}
